import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { api } from "../../api";
import usePageTitle from "../../hooks/usePageTitle";
import AppShell from "../AppShell";
import ImageWithFallback from "../ImageWithFallback";
import {
  getOnePieceSetImageCandidates,
  getOnePieceSetTheme,
  isOnePiecePromoSet,
  splitOnePieceSetsIntoSections,
} from "../../utils/onePieceSets";

const panelSx = {
  p: { xs: 2, md: 2.5 },
  borderRadius: 2.5,
  border: "1px solid",
  borderColor: "rgba(12, 35, 64, 0.08)",
  backgroundColor: "#ffffff",
  boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
};

const interactiveCardSx = {
  borderRadius: 2.25,
  border: "1px solid",
  borderColor: "rgba(12, 35, 64, 0.08)",
  backgroundColor: "#fbfcff",
  transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease, background-color 160ms ease",
  cursor: "pointer",
  textDecoration: "none",
  color: "inherit",
  display: "block",
  overflow: "hidden",
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: "0 16px 30px rgba(15, 23, 42, 0.12)",
    borderColor: "rgba(207, 139, 40, 0.2)",
    backgroundColor: "#ffffff",
  },
};

function PlaceholderImage({ label }) {
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "text.secondary",
        fontWeight: 700,
        letterSpacing: 0.3,
      }}
    >
      {label}
    </Box>
  );
}

function isHiddenOnePieceLegacySet(entry) {
  const code = String(entry?.code || "").trim().toUpperCase();
  const slug = String(entry?.slug || entry?.canonical_slug || "").trim().toLowerCase();
  const setName = String(entry?.set_name || "").trim().toLowerCase();
  return (
    code === "EB04"
    || slug === "eb04"
    || setName === "eb04"
    || setName === "extra booster 04"
  );
}

export default function OnePieceSetsPage() {
  usePageTitle("One Piece Sets");

  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("year_desc");

  useEffect(() => {
    let mounted = true;
    const timeoutId = setTimeout(async () => {
      try {
        console.log("ONE_PIECE_SETS_PAGE_FETCH", {
          searchTerm: String(searchTerm || "").trim(),
          sortBy,
        });
        setLoading(true);
        setError("");
        const { data } = await api.get("/api/one-piece/sets", {
          params: {
            search: String(searchTerm || "").trim(),
            sort: sortBy,
          },
        });
        if (!mounted) return;
        const nextSets = Array.isArray(data?.sets) ? data.sets : Array.isArray(data?.items) ? data.items : [];
        setSets(nextSets.filter((entry) => !isHiddenOnePieceLegacySet(entry)));
      } catch (err) {
        if (!mounted) return;
        setSets([]);
        setError(err?.response?.data?.message || err?.message || "Failed to load One Piece sets.");
      } finally {
        if (mounted) setLoading(false);
      }
    }, 200);

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, [searchTerm, sortBy]);

  const sections = useMemo(() => splitOnePieceSetsIntoSections(sets, sortBy), [sets, sortBy]);

  const renderSetGrid = (entries) => (
      <Grid container spacing={{ xs: 1.5, md: 2 }}>
      {entries.map((set) => {
        const theme = getOnePieceSetTheme(set);
        const imageCandidates = getOnePieceSetImageCandidates(set);
        const finalImageSrc = imageCandidates[0] || "";
        const setSlug = String(set?.slug || set?.canonical_slug || set?.code || "").trim().toLowerCase();
        if (String(set?.code || "").trim().toUpperCase() === "PRB01" || String(set?.code || "").trim().toUpperCase() === "PRB02") {
          console.log("[ONE_PIECE_SET_CARD_IMAGE_DEBUG]", {
            slug: set?.slug || "",
            canonical_slug: set?.canonical_slug || "",
            code: set?.code || "",
            set_name: set?.set_name || "",
            final_image_src: finalImageSrc,
          });
        }

        return (
          <Grid item xs={12} sm={6} md={4} lg={3} key={setSlug || set.set_name}>
            <Box component={RouterLink} to={`/one-piece/sets/${setSlug}`} sx={interactiveCardSx}>
              <Box
                sx={{
                  height: 220,
                  backgroundColor: theme.cardBg,
                  overflow: "hidden",
                  borderBottom: "1px solid rgba(12, 35, 64, 0.08)",
                }}
              >
                {imageCandidates.length ? (
                  <ImageWithFallback
                    src={finalImageSrc}
                    fallbackSrcs={imageCandidates.slice(1)}
                    alt={set.set_name}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                ) : (
                  <PlaceholderImage label={set.display_code || set.code || "One Piece"} />
                )}
              </Box>
              <Box
                sx={{
                  p: 1.6,
                  minHeight: 170,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1.05 }}>
                  <Chip label={set.display_code || set.code || "Promo"} size="small" sx={{ fontWeight: 700, bgcolor: theme.chipBg, color: "#10233f" }} />
                  {set.year ? <Chip label={`${set.year}`} size="small" sx={{ fontWeight: 700 }} /> : null}
                  {isOnePiecePromoSet(set) ? (
                    <Chip label="Promo" size="small" color="warning" sx={{ fontWeight: 700 }} />
                  ) : null}
                </Stack>
                <Typography variant="subtitle1" sx={{ fontWeight: 900, color: "#10233f", mb: 0.4, lineHeight: 1.3 }}>
                  {set.set_name}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.5 }}>
                  {set.item_count ? `${set.item_count} cards in inventory.` : "Set page available for browsing."}
                </Typography>
              </Box>
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );

  return (
    <AppShell>
      <Box sx={{ px: { xs: 0, md: 1 }, py: { xs: 1, md: 1.5 } }}>
        <Stack spacing={{ xs: 2.5, md: 3 }}>
          <Paper
            elevation={0}
            sx={{
              ...panelSx,
              p: { xs: 2.5, md: 3.25 },
              background: "linear-gradient(135deg, #0d1f33 0%, #173a5f 56%, #d48d34 100%)",
              borderColor: "rgba(255,255,255,0.10)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "radial-gradient(circle at top right, rgba(255,255,255,0.14), transparent 30%), radial-gradient(circle at bottom left, rgba(255,214,140,0.14), transparent 28%)",
                pointerEvents: "none",
              }}
            />
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Typography
                variant="overline"
                sx={{
                  display: "block",
                  color: "rgba(255,245,228,0.76)",
                  fontWeight: 800,
                  letterSpacing: 1.15,
                  mb: 1,
                }}
              >
                Collector Set Guide
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  color: "#ffffff",
                  fontWeight: 900,
                  lineHeight: 1.08,
                  mb: 1.1,
                  maxWidth: 760,
                  fontSize: { xs: "2rem", md: "2.7rem" },
                }}
              >
                One Piece Sets
              </Typography>
              <Typography
                sx={{
                  color: "rgba(255,244,226,0.84)",
                  maxWidth: 860,
                  lineHeight: 1.65,
                  fontSize: { xs: "0.98rem", md: "1.02rem" },
                }}
              >
                Browse One Piece sets, starter decks, premium boosters, and promos in one place, sorted with the newest releases first.
              </Typography>
            </Box>
          </Paper>

          <Paper elevation={0} sx={panelSx}>
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "stretch", sm: "center" },
                justifyContent: "space-between",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1.5,
                mb: 2.25,
              }}
            >
              <TextField
                label="Search Sets"
                placeholder="Search by set name, code, or year"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                fullWidth
              />
              <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 220 } }}>
                <InputLabel id="one-piece-set-sort-label">Sort Sets</InputLabel>
                <Select
                  labelId="one-piece-set-sort-label"
                  value={sortBy}
                  label="Sort Sets"
                  onChange={(event) => setSortBy(event.target.value)}
                >
                  <MenuItem value="name_asc">Name A-Z</MenuItem>
                  <MenuItem value="name_desc">Name Z-A</MenuItem>
                  <MenuItem value="year_desc">Newest First</MenuItem>
                  <MenuItem value="year_asc">Oldest First</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {loading ? (
              <Stack spacing={1.5} alignItems="center" justifyContent="center" sx={{ py: { xs: 5, md: 7 } }}>
                <CircularProgress size={34} />
                <Typography variant="body1" sx={{ color: "text.secondary" }}>
                  Loading One Piece sets...
                </Typography>
              </Stack>
            ) : error ? (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            ) : (
              <>
                <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 700, mb: 2 }}>
                  {sets.length} {sets.length === 1 ? "Set" : "Sets"}
                </Typography>
                <Stack spacing={3.5}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 900, color: "#10233f", mb: 1.75 }}>
                      Sets
                    </Typography>
                    {sections.sets.length ? (
                      renderSetGrid(sections.sets)
                    ) : (
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        No One Piece sets matched your search.
                      </Alert>
                    )}
                  </Box>

                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 900, color: "#10233f", mt: 1, mb: 1.75 }}>
                      Starter Decks
                    </Typography>
                    {sections.starter_decks.length ? (
                      renderSetGrid(sections.starter_decks)
                    ) : (
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        No starter decks matched your search.
                      </Alert>
                    )}
                  </Box>
                </Stack>
              </>
            )}
          </Paper>
        </Stack>
      </Box>
    </AppShell>
  );
}
